import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ProfileResults from "./ProfileResults";
import CityAutocomplete from "./CityAutocomplete";
import { trackEvent } from "../lib/analytics";
import { parseApiError } from "../lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  date: "",
  time: "",
  place: null, // { name, country, lat, lng, tz, display, country_code }
};

export default function BlueprintGenerator() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  // Hydrate profile from the email access link (?profile=) or localStorage
  useEffect(() => {
    const urlId = new URLSearchParams(window.location.search).get("profile");
    if (urlId) {
      try { window.localStorage.setItem("kl_profile_id", urlId); } catch (_) {}
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    const storedId = urlId || window.localStorage.getItem("kl_profile_id");
    if (!storedId) return;
    axios.get(`${API}/profile/${storedId}`)
      .then(async (r) => {
        if (!r.data?.id) return;
        let p = r.data;
        if (p.generation_status === "processing") {
          setLoading(true);
          try { p = await pollUntilReady(p.id); } finally { setLoading(false); }
        }
        setProfile(p);
        // Scroll to results once it's painted
        setTimeout(() => {
          const el = document.getElementById("results");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      })
      .catch(() => {
        window.localStorage.removeItem("kl_profile_id");
      });
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Poll the profile until the background AI synthesis is done (~30-90s).
  const pollUntilReady = async (id) => {
    const deadline = Date.now() + 4 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const r = await axios.get(`${API}/profile/${id}`, { timeout: 20000 });
        if (r.data?.generation_status === "ready") return r.data;
      } catch (_) { /* transient network error — keep polling */ }
    }
    throw new Error("Your blueprint is taking longer than expected. Please refresh in a moment — it will be waiting for you.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.date || !form.time || !form.place) {
      setError("Please complete every field, including selecting a city from the dropdown.");
      return;
    }
    trackEvent("blueprint_started");
    const [y, m, d] = form.date.split("-").map(Number);
    const [hh, mm] = form.time.split(":").map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) {
      setError("Please enter a valid birth date and time.");
      return;
    }
    const birth = new Date(y, m - 1, d);
    if (y < 1900 || birth > new Date()) {
      setError("Please enter a valid birth date — it must be between 1900 and today.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/profile/generate`, {
        name: form.name,
        year: y, month: m, day: d, hour: hh, minute: mm,
        city: form.place.display,
        lat: form.place.lat,
        lng: form.place.lng,
        tz: form.place.tz,
        country: form.place.country,
      }, { timeout: 45000 });
      try { window.localStorage.setItem("kl_profile_id", r.data.id); } catch (_) {}
      const ready = r.data.generation_status === "ready" ? r.data : await pollUntilReady(r.data.id);
      setProfile(ready);
      toast.success("Your blueprint is ready.");
      setTimeout(() => {
        const el = document.getElementById("results");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      const msg = parseApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="blueprint"
      data-testid="blueprint-section"
      className="relative py-24 md:py-32 lg:py-40 px-6 md:px-12"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="font-sans text-xs sm:text-sm tracking-overline text-[#D4AF37] mb-6">
            01 — the blueprint
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-white">
            Your journey <span className="italic gold-text">starts here.</span>
          </h2>
          <p className="mt-6 font-sans text-base sm:text-lg text-slate-300 leading-relaxed">
            Enter your birth details to unlock a personalised guide that weaves together your Natal Chart,
            Human Design, and Gene Keys into one integrated blueprint. Uncover the key themes, natural gifts,
            strengths, and growth opportunities that define your unique path, empowering you to move through
            life with greater self-awareness, authenticity, and purpose.
          </p>
        </motion.div>

        {/* Form card */}
        {!profile && (
          <motion.form
            data-testid="blueprint-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-16 glass-card rounded-3xl p-8 md:p-12 max-w-3xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Your name">
                <input
                  data-testid="form-name-input"
                  type="text"
                  className="cosmic-input rounded-xl px-4 py-3 w-full font-sans text-base"
                  placeholder="Selene Moon"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                />
              </Field>

              <Field label="Place of birth">
                <CityAutocomplete
                  value={form.place}
                  onChange={(v) => update("place", v)}
                  testid="form-city-autocomplete"
                />
              </Field>

              <Field label="Date of birth">
                <input
                  data-testid="form-date-input"
                  type="date"
                  className="cosmic-input rounded-xl px-4 py-3 w-full font-sans text-base"
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                />
              </Field>

              <Field label="Time of birth (24h, local)">
                <input
                  data-testid="form-time-input"
                  type="time"
                  className="cosmic-input rounded-xl px-4 py-3 w-full font-sans text-base"
                  value={form.time}
                  onChange={(e) => update("time", e.target.value)}
                />
              </Field>
            </div>

            {error && (
              <p data-testid="form-error" className="mt-6 text-sm text-red-300/90 font-sans">{error}</p>
            )}

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6">
              <p className="text-xs text-slate-400 font-sans max-w-sm">
                Your data stays private. We use Swiss Ephemeris precision and the Rave Mandala for an accurate read.
              </p>
              <button
                data-testid="form-submit-button"
                type="submit"
                disabled={loading}
                className="btn-gold rounded-full px-10 py-4 text-sm flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Channeling...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Reveal my blueprint
                  </>
                )}
              </button>
            </div>
          </motion.form>
        )}

        {/* Loading overlay */}
        <AnimatePresence>
          {loading && <CosmicLoader />}
        </AnimatePresence>

        {/* Results */}
        {profile && (
          <ProfileResults
            profile={profile}
            onReset={() => {
              try { window.localStorage.removeItem("kl_profile_id"); } catch (_) {}
              setProfile(null);
              setForm(initialForm);
            }}
          />
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-sans text-[11px] uppercase tracking-overline text-slate-400 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function CosmicLoader() {
  const STATUS = useMemo(() => [
    { title: "Aligning the heavens",     hint: "tropical chart · placidus houses · aspects" },
    { title: "Reading your stars",       hint: "swiss ephemeris precision · 12 houses" },
    { title: "Decoding your design",     hint: "rave mandala · 9 centers · 36 channels" },
    { title: "Weaving the gene keys",    hint: "shadow → gift → siddhi · 4 prime gifts" },
    { title: "Channeling your synthesis",hint: "weaving your unique soul portrait" },
    { title: "Almost there",             hint: "the final touches of light" },
  ], []);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % STATUS.length), 4000);
    return () => clearInterval(id);
  }, [STATUS]);

  // Ring geometry
  const R = 78;
  const C = 2 * Math.PI * R; // ~490

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-[#040508]/85 backdrop-blur-xl flex items-center justify-center px-6"
      data-testid="cosmic-loader"
    >
      <div className="text-center">
        <div className="relative w-52 h-52 mx-auto">
          {/* Outer halo glow */}
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_70%)] blur-2xl" />

          {/* Progress ring (gold arc that fills clockwise) */}
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="-100 -100 200 200">
            <defs>
              <linearGradient id="goldGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#f5e29a" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#B8860B" />
              </linearGradient>
            </defs>
            {/* Static track */}
            <circle cx="0" cy="0" r={R} fill="none" stroke="rgba(212,175,55,0.10)" strokeWidth="2" />
            {/* Animated arc */}
            <circle
              cx="0" cy="0" r={R}
              fill="none"
              stroke="url(#goldGrad)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={C}
              style={{
                strokeDashoffset: C,
                animation: "kl-fill 38s cubic-bezier(0.22, 1, 0.36, 1) forwards",
              }}
            />
          </svg>

          {/* Zodiac ticks ring (static) */}
          <svg className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)]" viewBox="-100 -100 200 200">
            <circle cx="0" cy="0" r="78" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * 30 * Math.PI) / 180;
              const x1 = 70 * Math.cos(a), y1 = 70 * Math.sin(a);
              const x2 = 78 * Math.cos(a), y2 = 78 * Math.sin(a);
              return (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke="rgba(212,175,55,0.5)" strokeWidth="1" />
              );
            })}
          </svg>

          {/* Orbiting planets (counter-spin) */}
          <div className="absolute inset-10 animate-spin-rev">
            {[0, 60, 120, 180, 240, 300].map((deg, i) => {
              const a = (deg * Math.PI) / 180;
              const r = 56;
              const left = `calc(50% + ${r * Math.cos(a)}px - 4px)`;
              const top  = `calc(50% + ${r * Math.sin(a)}px - 4px)`;
              return (
                <span
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_10px_#D4AF37]"
                  style={{ left, top, opacity: 0.55 + (i % 3) * 0.15 }}
                />
              );
            })}
          </div>

          {/* Inner pulsing star */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <span
                className="absolute inset-0 -m-3 rounded-full bg-[#D4AF37]/20 blur-xl"
                style={{ animation: "kl-pulse 2.4s ease-in-out infinite" }}
              />
              <Sparkles className="relative w-8 h-8 text-[#D4AF37]" />
            </div>
          </div>
        </div>

        {/* Status with crossfade */}
        <div className="mt-10 grid place-items-center min-h-[64px]">
          {STATUS.map((s, i) => (
            <div
              key={i}
              className={`col-start-1 row-start-1 transition-all duration-700 ${
                idx === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
              }`}
              aria-hidden={idx !== i}
            >
              <p className="font-serif italic text-2xl sm:text-3xl text-white">{s.title}…</p>
              <p className="mt-2 text-[11px] tracking-overline uppercase text-slate-400">{s.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Component-scoped keyframes */}
      <style>{`
        @keyframes kl-fill {
          to { stroke-dashoffset: 25; }
        }
        @keyframes kl-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.6; }
          50%      { transform: scale(1.4); opacity: 1;   }
        }
      `}</style>
    </motion.div>
  );
}
